import { type RefObject, useEffect, useState } from 'react';
import { env } from '@/lib/env';
import { useHighPerformanceMode } from '@/hooks/use-high-performance-mode';

const unloadEnabled = (() => {
  try {
    const v = String(env.NEXT_PUBLIC_UNLOAD_OFFSCREEN_MEDIA ?? '').trim().toLowerCase();
    if (!v) return true;
    return !(v === 'false' || v === '0' || v === 'no' || v === 'off');
  } catch {
    return true;
  }
})();

/**
 * Track whether a DOM element is inside (or near) the browser viewport via
 * IntersectionObserver.  Used to defer heavy resource loading (e.g. <video>
 * src) for off-screen elements.
 *
 * Returns `true` (i.e. "load the media") in any of these cases:
 * - The element is within `rootMargin` of the viewport (or the ref is empty —
 *   conservative default so new / contentless nodes don't start "hidden").
 * - `NEXT_PUBLIC_UNLOAD_OFFSCREEN_MEDIA` is `'false'` → never unload.
 * - "Render All Nodes" (high-performance mode) is enabled → since the user opted
 *   to render every node, keep all media loaded too (no viewport-based unload).
 *
 * Otherwise returns `false` for off-screen elements so callers can drop the
 * media `src` and free memory/bandwidth.
 */
export function useInViewport(
  ref: RefObject<Element | null>,
  key: string | undefined,
  // 提前量：元素进入视口前 900px 即开始加载媒体（原 300px），减少“滚到才出图”的等待。
  rootMargin = '900px',
): boolean {
  const [visible, setVisible] = useState(true);
  // “渲染全部节点”开启时，不再按视口懒加载/卸载媒体——既然用户选择渲染全部节点，
  // 就让媒体也全部常驻加载（符合直觉，避免“开了开关仍要滚到视口才加载”）。
  const [renderAllNodes] = useHighPerformanceMode();
  const alwaysLoad = !unloadEnabled || renderAllNodes;

  useEffect(() => {
    if (alwaysLoad) {
      setVisible(true);
      return;
    }
    const el = ref.current;
    if (!el) {
      setVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => setVisible(entry.isIntersecting),
      { rootMargin },
    );
    observer.observe(el);
    return () => observer.disconnect();
    // `ref` is a stable RefObject — reconnect via `key` when the observed
    // element might have been swapped (e.g. URL change → new <video>).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, rootMargin, alwaysLoad]);

  if (alwaysLoad) return true;
  return visible;
}
